import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement,
    Filler
} from 'chart.js';
import { FaMagic } from 'react-icons/fa'; // Nice modern AI icon
import './Analytics.css';

// Registered Filler for sleek area-under-the-line styling
ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, LineElement, PointElement, Filler);

const Analytics = () => {
    const navigate = useNavigate();
    const [userTier, setUserTier] = useState(null); 
    const [loadingTier, setLoadingTier] = useState(true);

    const [summary, setSummary] = useState({
        totalCards: 0,
        totalContacts: 0,
        totalAccessLogs: 0
    });

    const [accessStats, setAccessStats] = useState([]);
    const [recentContacts, setRecentContacts] = useState([]);
    const [trendSummary, setTrendSummary] = useState('');
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/api/users/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserTier(response.data.tier); 
            } catch (error) {
                console.error('Error verifying user tier:', error);
                setUserTier('free'); 
            } finally {
                setLoadingTier(false); 
            }
        };
        fetchUserProfile();
    }, [API_URL]);
    
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        const fetchTrendSummary = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/api/analytics/summary-ai`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setTrendSummary(response.data.summary);
            } catch (error) {
                console.error('Failed to fetch AI summary:', error);
            }
        };

        const fetchSummary = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/api/analytics/summary`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSummary(response.data);
            } catch (error) {
                console.error('Error fetching analytics summary:', error);
            }
        };
    
        const fetchAccessStats = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/api/analytics/access-stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setAccessStats(response.data);
            } catch (error) {
                console.error('Error fetching access stats:', error);
            }
        };
    
        const fetchRecentContacts = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/api/analytics/recent-contacts`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setRecentContacts(response.data);
            } catch (error) {
                console.error('Error fetching recent contacts:', error);
            }
        };
    
        if (userTier && userTier !== 'free') {
            fetchSummary();
            fetchAccessStats();
            fetchRecentContacts();
            fetchTrendSummary();
        }
    }, [API_URL, userTier, navigate]);

    if (loadingTier) {
        return (
            <div className="analytics-container loading-page">
                <h2>Verifying Account Status...</h2>
            </div>
        );
    }
    
    if (userTier === 'free') {
        return (
            <div className="analytics-container premium-lockout-page">
                <h2>Analytics Dashboard</h2>
                <div className="analytics-paywall-box">
                    <div className="lock-icon">📈</div>
                    <h3>Unlock Premium Analytics</h3>
                    <p>
                        Track your network growth in real-time. Upgrading unlocks tracking 
                        trends over time, AI-powered summary insights, and downloadable contact data.
                    </p>
                    <button onClick={() => navigate('/pricing')} className="upgrade-cta-button">
                        View Premium Plans
                    </button>
                </div>
            </div>
        );
    }

    const downloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/analytics/download`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` }
            });
    
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'analytics.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading CSV:', error);
        }
    };    

    // 🎨 Beautifully Restyled Line Graph Setup
    const chartData = {
        labels: accessStats.map(stat => stat.date),
        datasets: [
            {
                label: 'Card Accesses',
                data: accessStats.map(stat => stat.count),
                borderColor: '#2563eb', // Clean signature corporate blue
                backgroundColor: 'rgba(37, 99, 235, 0.06)', // Soft minimalist layout fill tint
                fill: true,
                tension: 0.35, // Smooth curves instead of rigid geometric lines
                pointBackgroundColor: '#2563eb',
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // Cleaner to let the chart header speak for itself
            },
            tooltip: {
                backgroundColor: '#0f172a',
                titleFont: { family: 'Montserrat', size: 13, weight: '600' },
                bodyFont: { family: 'Montserrat', size: 13 },
                padding: 12,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    label: function (tooltipItem) {
                        return `Views: ${tooltipItem.raw}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { family: 'Montserrat', size: 11 }, color: '#64748b' }
            },
            y: {
                grid: { color: '#f1f5f9' },
                border: { dash: [5, 5] }, // Dashed line layout accent gridlines
                ticks: { font: { family: 'Montserrat', size: 11 }, color: '#64748b', precision: 0 }
            }
        }
    };

    return (
        <div className="analytics-container">
            <h2 className="analytics-main-title">Analytics Dashboard</h2>
            
            <div className="summary">
                <div className="summary-item">
                    <h3>Total Cards</h3>
                    <p>{summary.totalCards}</p>
                </div>
                <div className="summary-item">
                    <h3>Total Contacts</h3>
                    <p>{summary.totalContacts}</p>
                </div>
                <div className="summary-item">
                    <h3>Total Access Logs</h3>
                    <p>{summary.totalAccessLogs}</p>
                </div>
            </div>

            <div className="chart-card-wrapper">
                <div className="chart-card-header">
                    <h3>Card Traffic (Last 30 Days)</h3>
                    <button onClick={downloadCSV} className="download-button">
                        Download CSV
                    </button>
                </div>
                <div className="chart-container">
                    <Line data={chartData} options={chartOptions} />
                </div>
            </div>

            {/* 🔮 Beautiful New Container Block for AI Insights */}
            <div className="trend-summary-card">
                <div className="ai-header-bar">
                    <div className="ai-title-group">
                        <FaMagic className="ai-sparkle-icon" />
                        <h3>AI Access Insights</h3>
                    </div>
                    <span className="ai-badge">Premium Feature</span>
                </div>
                <p className="ai-summary-text">{trendSummary || 'Analyzing profile metrics...'}</p>
            </div>

            <div className="recent-contacts">
                <h3>Recently Added Contacts (Last 30 Days)</h3>
                {recentContacts.length === 0 ? (
                    <p className="no-contacts-text">No contacts added yet in this timeframe.</p>
                ) : (
                    <ul>
                        {recentContacts.map((contact, index) => (
                            <li key={contact._id || index}>
                                <div className="contact-info">
                                    <strong>{contact.name}</strong>
                                    <span>{contact.position} {contact.companyName && `at ${contact.companyName}`}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default Analytics;